import {
	MouseEvent,
	ReactElement,
	RefObject,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState
} from "react";
import { TextInput, TextInputHandle } from "../TextInput/TextInput";
import { DropOver } from "../DropOver/DropOver";
import {
	Calendar,
	CalendarViewKind,
	useCalendar
} from "../Calendar/Calendar";
import { DropOverInput, DropOverInputAction } from "../DropOverInput/DropOverInput";
import styles from "./DatePicker.module.less";
import textInputStyles from "../TextInput/TextInput.module.less";
import { isAndroid, isIos } from "client_legacy/ndaw/mobile";
import {
	setTrackingId,
	generateTrackingId,
	isNotNil,
	ensureNotNil
} from "client/Misc/utils";
import classNames from "classnames";
import { never } from "client_legacy/ndaw/errors";
import {
	TimeZone,
	getSystemTimeZone
} from "client/DateAndTime/timezones";
import {
	addDays,
	getStartOfDay,
	isValidDate,
	millisecondsInAMinute
} from "client/DateAndTime/dateTimeUtils";
import { getCurrentCulture, localize } from "client_legacy/ndaw/culture";
import { DateOnly } from "client/DateAndTime/dateOnly";
import { convertToDateOnly, convertToDateTime } from "client/DateAndTime/dateUtils";
import { TimeOnly } from "client/DateAndTime/timeOnly";
import {
	convertToTimeOnly,
	convertToDateTime as convertTimeToDateTime
} from "client/DateAndTime/timeUtils";
import {
	MediaQueryKind,
	useIsMounted,
	useStableImperativeCallback,
	useWorkspaceMediaQuery
} from "client/Hooks";
import { ResponsiveMenu } from "../ResponsiveMenu/ResponsiveMenu";
import { DateTime } from "luxon";
import { NoUniqueInputIdContext, UniqueInputIdContext } from "client/Contexts/UniqueInputIdContext";
import { shallowComparison } from "client/Misc/compareUtils";
import { BaseTimePicker, BaseTimePickerProps } from "./BaseTimePicker";

type DatePickerMode = "date" | "datetime";

export type DatePickerProps = {
	value: Date | null;
	isInvalid?: boolean;
	onChange(value: Date | null): void;
	mode?: DatePickerMode;
	isDisabled?: boolean;
	datePlaceholder?: string;
	timePlaceholder?: string;
	trackingId: string;
	timezone: TimeZone;
	responsiveBehavior?: "nowrap" | "wrap";
	minDate?: Date;
	maxDate?: Date;
};

const LUXON_DATE_FORMAT = DateTime.DATE_SHORT;

const systemTimezone: TimeZone = getSystemTimeZone(getCurrentCulture());

export const DatePicker = (props: DatePickerProps): ReactElement => {
	const {
		value,
		isInvalid = false,
		onChange,
		mode = "datetime",
		isDisabled = false,
		timePlaceholder,
		datePlaceholder,
		trackingId,
		timezone,
		responsiveBehavior = "wrap",
		minDate,
		maxDate
	} = props;

	const effectiveMaxDate: Date | undefined = useMemo(
		(): Date | undefined => {
			if (
				isNotNil(minDate)
					&& isValidDate(minDate)
					&& isNotNil(maxDate)
					&& isValidDate(maxDate)
					&& maxDate.getTime() < minDate.getTime()
			) {
				console.warn("DatePicker: maxDate is before minDate; ignoring maxDate.");

				return undefined;
			}

			return maxDate;
		},
		[minDate, maxDate]
	);

	const dateOnlyRef: RefObject<DateOnly | null> = useRef<DateOnly>(null);
	const timeOnlyRef: RefObject<TimeOnly | null> = useRef<TimeOnly>(null);

	useEffect(
		(): void => {
			if (!isValidDate(value)) {
				return;
			}

			if (value === null) {
				dateOnlyRef.current = null;
				timeOnlyRef.current = null;
			} else {
				dateOnlyRef.current = convertToDateOnly(value, timezone);
				timeOnlyRef.current = convertToTimeOnly(value, timezone);
			}
		},
		[timezone, value]
	);

	const shouldRenderNative: boolean = useMemo(
		() => {
			const isMobileDevice: boolean = isAndroid() || isIos();
			const doesUseSystemTimezone: boolean = timezone.identifier === systemTimezone.identifier;

			return isMobileDevice && doesUseSystemTimezone;
		},
		[timezone.identifier]
	);

	const dateOnlyFromValue = useMemo(
		(): OptionalModel<DateOnly | null> => {
			if (isValidDate(value)) {
				return {
					isSet: true,
					value: value === null
						? null
						: convertToDateOnly(value, timezone)
				};
			}

			return { isSet: false };
		},
		[timezone, value]
	);

	const dateOnlyValue: DateOnly | null = dateOnlyFromValue.isSet
		? dateOnlyFromValue.value
		: dateOnlyRef.current;

	const timeOnlyFromValue = useMemo(
		(): OptionalModel<TimeOnly | null> => {
			if (isValidDate(value)) {
				return {
					isSet: true,
					value: value === null
						? null
						: convertToTimeOnly(value, timezone)
				};
			}

			return { isSet: false };
		},
		[timezone, value]
	);

	const timeOnlyValue: TimeOnly | null = timeOnlyFromValue.isSet
		? timeOnlyFromValue.value
		: timeOnlyRef.current;

	const resolveValueFromDateOnly = useCallback(
		(dateOnly: DateOnly | null): Date | null => {
			switch(mode) {
				case "date":
					if (dateOnly === null) {
						return null;
					}

					if (dateOnly.isInvalid()) {
						return INVALID_DATE;
					}

					return convertToDateTime(dateOnly, timezone);
				case "datetime":
					if (dateOnly === null && timeOnlyRef.current === null) {
						return null;
					}

					if (dateOnly === null) {
						return INVALID_DATE;
					}

					const timeOnly: TimeOnly = timeOnlyRef.current === null
						? new TimeOnly(0, 0, 0, 0)
						: timeOnlyRef.current;

					if (dateOnly.isInvalid() || timeOnly.isInvalid()) {
						return INVALID_DATE;
					}

					return convertTimeToDateTime(
						timeOnly,
						timezone,
						convertToDateTime(
							dateOnly,
							timezone
						)
					);
				default:
					never(mode);
			}
		},
		[mode, timezone]
	);

	const dateOnlyChangeHandler = useCallback(
		(newValue: DateOnly | null): void => {
			dateOnlyRef.current = newValue;

			const newResolvedValue: Date | null = resolveValueFromDateOnly(newValue);

			if (shallowComparison(newResolvedValue, value)) {
				return;
			}

			onChange(newResolvedValue);
		},
		[
			onChange,
			resolveValueFromDateOnly,
			value
		]
	);

	const resolveValueFromTimeOnly = useCallback(
		(timeOnly: TimeOnly | null): Date | null => {
			if (timeOnly === null && dateOnlyRef.current === null) {
				return null;
			}

			if (timeOnly === null) {
				return INVALID_DATE;
			}

			const dateOnly: DateOnly = dateOnlyRef.current === null
				? convertToDateOnly(new Date(), timezone)
				: dateOnlyRef.current;

			if (timeOnly.isInvalid() || dateOnly.isInvalid()) {
				return INVALID_DATE;
			}

			return convertTimeToDateTime(
				timeOnly,
				timezone,
				convertToDateTime(
					dateOnly,
					timezone
				)
			);
		},
		[timezone]
	);

	const timeOnlyChangeHandler = useCallback(
		(newValue: TimeOnly | null): void => {
			timeOnlyRef.current = newValue;

			if (mode === "date") {
				return;
			}

			const newResolvedValue: Date | null = resolveValueFromTimeOnly(newValue);

			if (shallowComparison(newResolvedValue, value)) {
				return;
			}

			onChange(newResolvedValue);
		},
		[
			mode,
			onChange,
			resolveValueFromTimeOnly,
			value
		]
	);

	return (
		<div
			{...setTrackingId(
				trackingId,
				shouldRenderNative
					? "__native-picker"
					: "__custom-picker"
			)}
			className={
				classNames(
					styles.dateTimePickerWrapper,
					{
						[styles.dateTimePickerWrapper_noWrap]: responsiveBehavior === "nowrap"
					}
				)
			}
		>
			<DateOnlyPicker
				value={dateOnlyValue}
				onChange={dateOnlyChangeHandler}
				baseTrackingId={trackingId}
				isDisabled={isDisabled}
				isInvalid={isInvalid}
				shouldRenderNative={shouldRenderNative}
				timezone={timezone}
				placeholder={datePlaceholder}
				min={minDate}
				max={effectiveMaxDate}
			/>
			{mode === "datetime" && (
				<NoUniqueInputIdContext>
					<TimeOnlyPicker
						value={timeOnlyValue}
						onChange={timeOnlyChangeHandler}
						trackingId={trackingId}
						isDisabled={isDisabled}
						isInvalid={isInvalid}
						shouldRenderNative={shouldRenderNative}
						timezone={timezone}
						placeholder={timePlaceholder}
						date={dateOnlyValue}
						min={minDate}
						max={effectiveMaxDate}
					/>
				</NoUniqueInputIdContext>
			)}
		</div>
	);
};
DatePicker.displayName = "DatePicker";

const defaultDatePlaceholder: string = (
	new Intl.DateTimeFormat(getCurrentCulture(), LUXON_DATE_FORMAT)
).formatToParts(new Date(2023, 11, 30))
	.map(part => {
		switch (part.type) {
			case "month":
				return part.value
					.replace(/./g, "M");
			case "day":
				return part.value
					.replace(/./g, "D");
			case "year":
				return part.value
					.replace(/./g, "Y");
			case "literal":
				return part.value;
			default:
				return "";
		}
	}).join("");

type DateOnlyPickerProps = {
	value: DateOnly | null;
	onChange(newDate: DateOnly | null): void;
	shouldRenderNative: boolean;
	baseTrackingId?: string;
	isInvalid: boolean;
	isDisabled: boolean;
	placeholder?: string;
	timezone: TimeZone;
	min?: Date;
	max?: Date;
};

const DateOnlyPicker = (props: DateOnlyPickerProps): ReactElement => {
	const {
		value,
		onChange,
		shouldRenderNative,
		baseTrackingId,
		isInvalid,
		isDisabled,
		placeholder = defaultDatePlaceholder,
		timezone,
		min,
		max
	} = props;

	const [isDropOverOpen, setIsDropOverOpen] = useState<boolean>(false);
	const [rawValue, setRawValue] = useState<RawValueWithParsedDateOnly>({ raw: "", parsed: null });
	const [isEditingText, setIsEditingText] = useState<boolean>(false);
	const [hasFocus, setHasFocus] = useState<boolean>(false);

	const ref: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);
	const textInputRef: RefObject<TextInputHandle | null> = useRef<TextInputHandle>(null);
	const blurTimeoutHandleRef: RefObject<SetTimeoutHandle| undefined> = useRef<SetTimeoutHandle>(undefined);
	const isMounted: () => boolean = useIsMounted();
	const isResponsive: boolean = useWorkspaceMediaQuery(MediaQueryKind.Responsive);
	const { id } = useContext(UniqueInputIdContext);

	useEffect(
		(): void => {
			if (isEditingText || value?.isInvalid()) {
				return;
			}

			setRawValue(
				rawValue => {
					if (areDatesEqual(value, rawValue.parsed)) {
						return rawValue;
					}

					return {
						raw: isNotNil(value)
							? formatDateOnlyUsingLuxon(
								value,
								shouldRenderNative ? "iso" : LUXON_DATE_FORMAT
							)
							: "",
						parsed: value
					};
				}
			);
		},
		[
			isEditingText,
			shouldRenderNative,
			value
		]
	);

	const normalizedDateValue: Date | null = useMemo(
		(): Date | null => {
			return !value || value.isInvalid()
				? null
				: convertToDateTime(value, timezone);
		},
		[timezone, value]
	);

	const { view, onViewChange } = useCalendar(
		CalendarViewKind.Month,
		normalizedDateValue,
		timezone
	);

	const openDropOverHandler = useCallback(
		(): void => {
			onViewChange({
				viewKind: CalendarViewKind.Month,
				date: normalizedDateValue ?? new Date()
			});

			setIsDropOverOpen(true);
		},
		[normalizedDateValue, onViewChange]
	);

	const closeDropOverHandler = useCallback(
		(): void => {
			setIsDropOverOpen(false);
		},
		[]
	);

	const calendarChangeHandler = useCallback(
		(dateTime: Date): void => {
			const date: DateOnly = convertToDateOnly(dateTime, timezone);

			onViewChange({
				date: dateTime,
				viewKind: CalendarViewKind.Month
			});

			setIsEditingText(false);
			onChange(date);
			textInputRef.current
				?.focus();
			closeDropOverHandler();
		},
		[
			closeDropOverHandler,
			onChange,
			onViewChange,
			timezone,
			textInputRef
		]
	);

	const textInputChangeHandler = (newTextValue: string): void => {
		const newDateValue: DateOnly | null = parseDateOnlyUsingLuxon(
			newTextValue,
			shouldRenderNative
		);

		setIsEditingText(true);
		setRawValue({
			raw: newTextValue,
			parsed: newDateValue
		});

		if (newDateValue
			&& !newDateValue.isInvalid()
			&& view.viewKind === CalendarViewKind.Month
		) {
			onViewChange({
				viewKind: CalendarViewKind.Month,
				date: convertToDateTime(newDateValue, timezone)
			});
		}
	};

	const textInputBlurHandler = (): void => {
		setIsEditingText(false);
		onChange(rawValue.parsed);
	};

	const preventStealingFocusHandler = useCallback(
		(e: MouseEvent): void => {
			e.preventDefault();
		},
		[]
	);

	const focusHandler = useCallback(
		(): void => {
			if (blurTimeoutHandleRef.current) {
				clearTimeout(blurTimeoutHandleRef.current);
				blurTimeoutHandleRef.current = undefined;
			} else {
				setHasFocus(true);
			}
		},
		[]
	);

	const blurCallback = useCallback(
		(): void => {
			setHasFocus(false);
			closeDropOverHandler();
		},
		[closeDropOverHandler]
	);

	const stableBlurCallback: typeof blurCallback = useStableImperativeCallback(blurCallback);

	const blurHandler = useCallback(
		(): void => {
			blurTimeoutHandleRef.current = setTimeout(
				(): void => {
					if (!isMounted()) {
						return;
					}

					blurTimeoutHandleRef.current = undefined;
					stableBlurCallback();
				},
				0
			);
		},
		[stableBlurCallback, isMounted]
	);

	const actions = useMemo(
		(): readonly DropOverInputAction[] => {
			return [
				{
					icon: "calendar",
					onClick: () => {
						if (isDropOverOpen) {
							closeDropOverHandler();
						} else {
							openDropOverHandler();
						}
					}
				}
			];
		},
		[
			closeDropOverHandler,
			isDropOverOpen,
			openDropOverHandler
		]
	);

	const dateDropOverOutsideIgnoreRefs = useMemo(
		(): readonly RefObject<HTMLDivElement | null>[] => [ref],
		[]
	);

	if (shouldRenderNative) {
		return (
			<input
				{...setTrackingId(baseTrackingId, "__date")}
				id={id}
				type="date"
				className={
					classNames(
						textInputStyles.root,
						{
							[textInputStyles.root_isInvalid]: isInvalid
						}
					)
				}
				value={rawValue.raw}
				onChange={(e) => textInputChangeHandler(e.target.value)}
				onBlur={textInputBlurHandler}
				disabled={isDisabled}
			/>
		);
	}

	const renderCalendar = (): ReactElement => {
		return (
			<Calendar
				view={view}
				onViewChange={onViewChange}
				value={normalizedDateValue}
				onChange={calendarChangeHandler}
				trackingId={generateTrackingId(baseTrackingId, "__calendar")}
				timezone={timezone}
				minDate={min}
				maxDate={max}
			/>
		);
	};

	return (
		<div
			onFocus={focusHandler}
			onBlur={blurHandler}
		>
			<div
				ref={ref}
				onClick={openDropOverHandler}
			>
				<DropOverInput
					isActive={hasFocus}
					actions={actions}
					isInvalid={isInvalid}
					isDisabled={isDisabled}
				>
					<TextInput
						value={rawValue.raw}
						isBorderless
						height="fill"
						onChange={(s) => textInputChangeHandler(s)}
						onBlur={textInputBlurHandler}
						attachedOn="right"
						trackingId={generateTrackingId(baseTrackingId, "__date")}
						isDisabled={isDisabled}
						placeholder={placeholder}
						ref={textInputRef}
					/>
				</DropOverInput>
			</div>
			{isDropOverOpen
				&& !isDisabled
				&& ref.current
				&& (
					<DropOver
						target={ref.current}
						targetAlignment="br"
						contentAlignment="tr"
						outsideClickIgnoreRefs={dateDropOverOutsideIgnoreRefs}
						onClose={closeDropOverHandler}
						closeOnMouseDown
					>
						{isResponsive
							? (
								<ResponsiveMenu
									onClose={closeDropOverHandler}
									trackingId={generateTrackingId(baseTrackingId, "__dropover")}
									onMouseDown={preventStealingFocusHandler}
								>
									<ResponsiveMenu.Header title={localize("_Date")} />

									<ResponsiveMenu.Body>
										{renderCalendar()}
									</ResponsiveMenu.Body>
								</ResponsiveMenu>
							)
							: (
								<div
									{...setTrackingId(baseTrackingId, "__dropover")}
									className={styles.dateDropOver}
									onMouseDown={preventStealingFocusHandler}
								>
									{renderCalendar()}
								</div>
							)
						}
					</DropOver>
				)
			}
		</div>
	);
};
DateOnlyPicker.displayName = "DateOnlyPicker";

type TimeOnlyPickerProps = Omit<BaseTimePickerProps, "timeListOptions"> & {
	date: DateOnly | null;
	timezone: TimeZone;
	min?: Date;
	max?: Date;
};

const TimeOnlyPicker = (props: TimeOnlyPickerProps): ReactElement => {
	const {
		date,
		timezone,
		trackingId,
		min,
		max,
		...otherProps
	} = props;

	const timeListOptions = useMemo(
		(): readonly TimeOnly[] => {
			const startOfTheRange: Date = !date || date.isInvalid()
				? getStartOfDay(
					new Date(),
					timezone
				)
				: convertToDateTime(
					date,
					timezone
				);

			const endOfTheRange: Date = addDays(
				startOfTheRange,
				1,
				timezone
			);

			const dayStart: number = startOfTheRange.getTime();
			const dayEnd: number = endOfTheRange.getTime();

			const step: number = 30 * millisecondsInAMinute;

			const anchor: number = isNotNil(min) && isValidDate(min)
				? Math.max(dayStart, min.getTime())
				: dayStart;

			const items: TimeOnly[] = [];
			for (let i = anchor; i < dayEnd; i += step) {
				if (
					isNotNil(max)
						&& isValidDate(max)
						&& i > max.getTime()
				) {
					break;
				}

				const entry = new Date(i);
				items.push(
					convertToTimeOnly(entry, timezone)
				);
			}

			return items;
		},
		[
			date,
			timezone,
			min,
			max
		]
	);

	return (
		<BaseTimePicker
			{...otherProps}
			trackingId={generateTrackingId(trackingId, "__time")}
			timeListOptions={timeListOptions}
		/>
	);

};
TimeOnlyPicker.displayName = "TimeOnlyPicker";

function areDatesEqual(date1: DateOnly | null, date2: DateOnly | null): boolean {
	if (!date1 && !date2) {
		return true;
	}

	if (!date1 || !date2) {
		return false;
	}

	return date1.isEqualTo(date2);
}

function formatDateOnlyUsingLuxon(
	dateOnly: DateOnly,
	format: "iso" | Intl.DateTimeFormatOptions
): string {
	const dateTime: DateTime = DateTime.fromObject({
		year: dateOnly.year,
		month: dateOnly.month,
		day: dateOnly.day
	});

	return format === "iso"
		? ensureNotNil(dateTime.toISODate())
		: dateTime.toLocaleString(format);
}

const INVALID_DATE: Date = new Date("");

function parseDateOnlyUsingLuxon(
	value: string,
	isIso: boolean
): DateOnly | null {
	if (!value) {
		return null;
	}

	const luxonValue: DateTime = isIso
		? DateTime.fromISO(value)
		: DateTime.fromFormat(
			value,
			// Luxon doesn't provide a utility function to parse from Intl.DateTimeFormatOptions
			// we are using token formats for now to remediate to that
			"D"
		);

	if (!luxonValue.isValid) {
		return DateOnly.InvalidValue;
	}

	return new DateOnly(
		luxonValue.year,
		luxonValue.month - 1,
		luxonValue.day
	);
}

type RawValueWithParsedDateOnly = {
	raw: string;
	parsed: DateOnly | null;
};